import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryApi } from '@reduxjs/toolkit/query/react';
import _ from 'lodash';

import { BSAEnv, PendingItemStatus } from '@appTypes/enums';
import type {
  AddItemPurchaseOrderReq,
  AdvancementHistoryItem,
  AdvancementHistoryRequest,
  GetActivitesRequest,
  GetActivitesRequestParams,
  GetActivitiesResponse,
  GetAdventureDetailsResponse,
  GetAdventureRequirementsReq,
  GetAdventureRequirementsRes,
  GetAdventuresReq,
  GetAdventuresRes,
  GetCompletePersonProfileRes,
  GetGeneralAdvancementDetailsResponse,
  GetMeritBadgeDetailsResponse,
  GetMeritBadgeRequirementsReq,
  GetMeritBadgeRequirementsRes,
  GetOrgAdvancementsReadyToAwardParams,
  GetOrgAdvancementsReadyToAwardRequest,
  GetOrgAdvancementsReadyToAwardResponse,
  GetOrgPositionsResponse,
  GetOrgProfileResponse,
  GetOrgSubUnitsResponse,
  GetPersonProfileResponse,
  GetPersonTrainingYPTResponse,
  GetPurchaseOrderDetailsResponse,
  GetRankDetailsResponse,
  GetRankRequirementsReq,
  GetRankRequirementsRes,
  GetRanksReq,
  GetRanksRes,
  GetRenewalRelationshipsRequest,
  GetRenewalRelationshipsResponse,
  GetSSElectiveDetailsResponse,
  GetUnitDenChiefsRes,
  GetUnitPurchaseOrdersRequest,
  GetUnitPurchaseOrdersResponse,
  GetUserActivitySummaryResponse,
  GetUserAdventureRequirementsReq,
  GetUserAdventureRequirementsRes,
  GetUserMeritBadgeRequirementsReq,
  GetUserMeritBadgeRequirementsRes,
  GetUserMystProfileResponse,
  GetUserRankVersionRequirementsRes,
  GetUserRoleTypesRequest,
  GetUserRoleTypesResponse,
  GetUserSubscriptionResponse,
  GetYouthMeritBadgeDetailsRes,
  OrganizationActivitiesDashboard,
  OrganizationAdvDashboard,
  POSTSubscriptionRequest,
  PUTSubscriptionRequest,
  PostAssignFunctionalRequest,
  PostAssignFunctionalResponse,
  PostAwardRequest,
  PostAwardRequirementsRequest,
  PostAwardRequirementsResponse,
  PostAwardResponse,
  PostBulkMeritBadgeRequirementsReq,
  PostBulkMeritBadgeRequirementsRes,
  PostBulkYouthRankRequirementsReq,
  PostBulkYouthRankRequirementsRes,
  PostDeleteProfileInfoRequest,
  PostExpireFunctionalRequest,
  PostExpireFunctionalResponse,
  PostMembershipRegistrationsRequest,
  PostMembershipRegistrationsResponse,
  PostPermissionSlipProfilesRequest,
  PostPermissionSlipProfilesResponse,
  PostPictureUploadRequest,
  PostPictureUploadResponse,
  PostProfilePictureRequest,
  PostReassignPositionsRequest,
  PostReassignPositionsResponse,
  PostYouthAdventuresRequirementsRequest,
  PostYouthAdventuresRequirementsResponse,
  PostYouthMeritBadgesReq,
  PostYouthMeritBadgesRes,
  PutAkelaProfileRequest,
  PutSBProfileRequest,
  PutUpdateAwardsRequest,
  UpdateMembershipRegistrationRequest,
  UpdateMembershipRegistrationResponse,
  UpdatePurchaseOrderDetailsReq,
} from '@appTypes/esb';
import type { UnapproveAdvancementPayload } from '@appTypes/esb/advancements/unapprove';
import { API_ENV, ESB_API_URLS } from '@config/config';
import type { ConfigState } from '@config/types';
import { mapAdvancementReportDtoToModel } from '@modules/advancementReport/duck/services.utils';
import type { AdvancementReportData } from '@modules/advancementReport/types';
import { advancementTypes } from '@modules/shared';
import type { NormalizedOrgProfile } from '@modules/unit/types';
import { transformOrgProfile } from '@unit/duck/utils';

import { tagTypes } from './constants';
import { dynamicBaseQuery } from './utils';
import {
  type AdvancementDetailsArgs,
  type GeneralAdvancementDetailsArgs,
  getAdvancementDetailsQuery,
  getGeneralAdvancementDetailsQuery,
} from './utils/advancementDetailsObject';
import tagFactory from './utils/tagFactory';

function getESBBaseUrl(_args: unknown, api: BaseQueryApi): string {
  const state = api.getState() as Record<string, unknown>;

  const stateApiEnv = (state?.config as ConfigState)?.apiEnv as string;
  const finalApiEnv = (stateApiEnv ? stateApiEnv : API_ENV) as BSAEnv;

  const baseUrl = ESB_API_URLS[finalApiEnv];
  return baseUrl;
}

export const esbApi = createApi({
  reducerPath: 'esbApi',
  tagTypes: Object.keys(tagTypes),
  baseQuery: dynamicBaseQuery(getESBBaseUrl),
  endpoints: builder => ({
    /**
     * @esbEndpoint GET /persons/:personGuid/roleTypes
     */
    getRoleTypes: builder.query<
      GetUserRoleTypesResponse,
      {
        personGuid: string;
        params: GetUserRoleTypesRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/roleTypes`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error, arg) => {
        if (error || !result) return [];
        return [{ type: tagTypes.UserRoleTypes, id: arg.personGuid }];
      },
    }),
    /**
     * @esbEndpoint GET /organizations/v2/:orgGuid/profile
     */
    getOrganizationProfile: builder.query<
      NormalizedOrgProfile,
      {
        orgGuid: string;
      }
    >({
      query: ({ orgGuid }) => ({
        url: `/organizations/v2/${orgGuid}/profile`,
        method: 'GET',
      }),
      transformResponse: (result: GetOrgProfileResponse) =>
        transformOrgProfile(result),
      providesTags: (result, _error, { orgGuid }) =>
        result ? [{ type: tagTypes.OrganizationProfile, id: orgGuid }] : [],
    }),

    /**
     * @esbEndpoint GET /organizations/v2/:organizationGuid/advancementDashboard
     */
    getOrgAdvancementsDashboard: builder.query<
      OrganizationAdvDashboard,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.organizationGuid}/advancementDashboard`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.OrganizationAdvDashboard,
                id: arg.organizationGuid,
              },
            ],
    }),

    /**
     * @esbEndpoint GET /organizations/v2/:arg.organizationGuid/unitActivitiesDashboard
     */
    getOrgActivityDashboard: builder.query<
      OrganizationActivitiesDashboard,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.organizationGuid}/unitActivitiesDashboard`,
        method: 'GET',
        params: {
          completedActivities: true,
        },
      }),
      providesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.OrganizationActivitiesDashboard,
                id: arg.organizationGuid,
              },
            ],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/:advancementType/:advancementId
     */
    getGeneralAdvancementDetails: builder.query<
      GetGeneralAdvancementDetailsResponse,
      GeneralAdvancementDetailsArgs
    >(getGeneralAdvancementDetailsQuery()),
    /**
     * @esbEndpoint GET /advancements/v2/adventures/:advancementId
     */
    getAdventureDetails: builder.query<
      GetAdventureDetailsResponse,
      AdvancementDetailsArgs
    >(getAdvancementDetailsQuery(advancementTypes.ADVENTURES)),

    /**
     * @esbEndpoint GET /advancements/v2/meritBadges/:advancementId
     */
    getMeritBadgeDetails: builder.query<
      GetMeritBadgeDetailsResponse,
      AdvancementDetailsArgs
    >(getAdvancementDetailsQuery(advancementTypes.MERIT_BADGES)),
    /**
     * @esbEndpoint GET /advancements/v2/ranks/:advancementId
     */
    getRankDetails: builder.query<
      GetRankDetailsResponse,
      AdvancementDetailsArgs
    >(getAdvancementDetailsQuery(advancementTypes.RANKS)),

    /**
     * @esbEndpoint GET /advancements/v2/ssElectives/:advancementId
     */
    getSSElectiveDetails: builder.query<
      GetSSElectiveDetailsResponse,
      AdvancementDetailsArgs
    >(getAdvancementDetailsQuery(advancementTypes.SSELECTIVES)),
    /**
     * @esbEndpoint GET /advancements/v2/organization/{organizationGuid}/ranks/{rankId}/userRequirements
     */
    getBulkUserRankRequirements: builder.query<
      GetUserRankVersionRequirementsRes,
      {
        orgGuid: string;
        rankId: number;
        memberIds: number[];
      }
    >({
      query: arg => {
        const { orgGuid, rankId, memberIds } = arg;
        return {
          url: `/advancements/v2/organization/${orgGuid}/ranks/${rankId}/userRequirements`,
          method: 'GET',
          params: {
            memberId: memberIds,
          },
        };
      },
      providesTags: (result, error) =>
        result && !error
          ? result.map(rankInfo => ({
              type: tagTypes.YouthRankVersionDetails,
              id: `${rankInfo.userId}-${rankInfo.rankId}`,
            }))
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/organization/{organizationGuid}/adventures/{adventureId}/userRequirements
     */
    getBulkUserAdventureRequirements: builder.query<
      GetUserAdventureRequirementsRes,
      {
        orgGuid: string;
        adventureId: number;
        params: GetUserAdventureRequirementsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGuid}/adventures/${arg.adventureId}/userRequirements`,
        method: 'GET',
        params: {
          memberId: arg.params.memberId,
        },
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.AdventureUserRequirements,
                id: `${arg.orgGuid}-${arg.adventureId}`,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/organization/{organizationGuid}/meritBadges/{meritBadgeId}/userRequirements
     */
    getBulkUserMeritBadgeRequirements: builder.query<
      GetUserMeritBadgeRequirementsRes,
      {
        orgGuid: string;
        meritBadgeId: number;
        params: GetUserMeritBadgeRequirementsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGuid}/meritBadges/${arg.meritBadgeId}/userRequirements`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error) =>
        result && !error
          ? result.map(meritBadgeInfo => ({
              type: tagTypes.MeritBadgeUserRequirements,
              id: `${meritBadgeInfo.userId}`,
            }))
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/meritBadges/:meritBadgeId
     */
    getYouthMeritBadgeDetails: builder.query<
      GetYouthMeritBadgeDetailsRes,
      {
        userId: number;
        meritBadgeId: number;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/meritBadges/${arg.meritBadgeId}`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.YouthMeritBadge,
                id: `${arg.userId}-${arg.meritBadgeId}`,
              },
            ],
    }),

    /**
     * === Advancements V1
     */
    /**
     * @esbEndpoint GET /advancements/adventures/{adventureId}/requirements
     */
    getAdventureRequirements: builder.query<
      GetAdventureRequirementsRes,
      { adventureId: number; params: GetAdventureRequirementsReq }
    >({
      query: arg => ({
        url: `/advancements/adventures/${arg.adventureId}/requirements`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, _error, { adventureId }) =>
        result?.requirements
          ? [
              {
                type: tagTypes.AdventureRequirements,
                id: adventureId,
              },
            ]
          : [],
    }),
    /**
     * @esbEndpoint GET /advancements/meritBadges/{meritBadgeId}/requirements
     */
    getMeritBadgeRequirements: builder.query<
      GetMeritBadgeRequirementsRes,
      { meritBadgeId: number; params: GetMeritBadgeRequirementsReq }
    >({
      query: arg => ({
        url: `/advancements/meritBadges/${arg.meritBadgeId}/requirements`,
        method: 'GET',
        params: arg.params,
      }),
    }),

    /**
     * @esbEndpoint POST /advancements/youth/meritBadges
     */
    postYouthMeritBadges: builder.mutation<
      PostYouthMeritBadgesRes,
      PostYouthMeritBadgesReq
    >({
      query: body => ({
        url: `/advancements/youth/meritBadges`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const orgGuids = _.uniq(arg.map(item => item.organizationGuid));

        return orgGuids.flatMap(orgGuid => [
          {
            // invalidate the counts to approve tab
            type: tagTypes.OrganizationAdvDashboard,
            id: orgGuid,
          },
          {
            // refresh to approve list
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${orgGuid}`,
          },
        ]);
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/youth/adventures/{adventureId}/requirements
     */
    postYouthAdventuresRequirements: builder.mutation<
      PostYouthAdventuresRequirementsResponse,
      {
        adventureId: number;
        payload: PostYouthAdventuresRequirementsRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/adventures/${arg.adventureId}/requirements`,
        method: 'POST',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const orgGuids = _.uniq(arg.payload.map(item => item.organizationGuid));

        return orgGuids.flatMap(orgGuid => [
          {
            type: tagTypes.AdventureUserRequirements,
            id: `${orgGuid}-${arg.adventureId}`,
          },
          {
            // invalidate the counts to approve tab
            type: tagTypes.OrganizationAdvDashboard,
            id: orgGuid,
          },
          {
            // refresh to approve list
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${orgGuid}`,
          },
        ]);
      },
    }),

    /**
     * === Advancements V2
     */

    /**
     * @esbEndpoint GET /persons/v2/:userId/personprofile
     */
    getPersonProfile: builder.query<
      GetPersonProfileResponse,
      {
        userId: number;
      }
    >({
      query: ({ userId }) => ({
        url: `/persons/v2/${userId}/personprofile`,
        method: 'GET',
      }),
      providesTags: (result, _error, { userId }) =>
        result ? [{ type: tagTypes.PersonProfile, id: userId }] : [],
    }),

    /**
     * @esbEndpoint GET /persons/v2/{personGuid}/personProfile
     */
    getCompletePersonProfile: builder.query<
      GetCompletePersonProfileRes,
      {
        personGuid: string;
      }
    >({
      query: ({ personGuid }) => ({
        url: `/persons/v2/${personGuid}/personprofile`,
        method: 'GET',
      }),
      providesTags: (result, error, { personGuid }) =>
        result && !error
          ? [
              {
                type: tagTypes.CompletePersonProfile,
                id: personGuid,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /persons/:personGuid/subscriptions
     */
    getUserSubscriptions: builder.query<
      GetUserSubscriptionResponse,
      {
        personGuid: string;
      }
    >({
      query: ({ personGuid }) => ({
        url: `/persons/${personGuid}/subscriptions`,
        method: 'GET',
      }),
      providesTags: (result, _error, { personGuid }) =>
        result ? [{ type: tagTypes.PersonSubscriptions, id: personGuid }] : [],
    }),

    /**
     * @esbEndpoint GET /persons/:personGuid/profile
     */
    getUserMystProfile: builder.query<
      GetUserMystProfileResponse,
      {
        personGuid: string;
      }
    >({
      query: ({ personGuid }) => ({
        url: `/persons/${personGuid}/profile`,
        method: 'GET',
      }),
      providesTags: (result, _error, { personGuid }) =>
        result ? [{ type: tagTypes.PersonMystProfile, id: personGuid }] : [],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/youth/:userId/ranks/:rankId
     */
    getYouthRankDetails: builder.query({
      query: ({ rankId, userId }) => ({
        url: `/advancements/v2/youth/${userId}/ranks/${rankId}`,
        method: 'GET',
      }),
      providesTags: (result, _error, { userId, rankId }) =>
        result
          ? [{ type: tagTypes.YouthRankDetails, id: `${userId}-${rankId}` }]
          : [],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/youth/meritBadges/{meritBadgeId}/requirements
     */
    postBulkYouthMeritBadgeRequirements: builder.mutation<
      PostBulkMeritBadgeRequirementsRes,
      { meritBadgeId: number; payload: PostBulkMeritBadgeRequirementsReq }
    >({
      query: ({ meritBadgeId, payload }) => ({
        url: `/advancements/v2/youth/meritBadges/${meritBadgeId}/requirements`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const orgGuids = _.uniq(arg.payload.map(item => item.organizationGuid));

        return orgGuids.flatMap(orgGuid => [
          {
            // invalidate the counts to approve tab
            type: tagTypes.OrganizationAdvDashboard,
            id: orgGuid,
          },
          {
            // refresh to approve list
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${orgGuid}`,
          },
        ]);
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/youth/ranks/:rankId/requirements
     */
    postBulkYouthRankRequirements: builder.mutation<
      PostBulkYouthRankRequirementsRes,
      { rankId: number; payload: PostBulkYouthRankRequirementsReq }
    >({
      queryFn: async (arg, _api, _opts, baseQuery) => {
        const promises = _.chain(arg.payload)
          .groupBy(item => item.userId)
          .map(youthPayload => {
            const payload: PostBulkYouthRankRequirementsReq = youthPayload;
            return baseQuery({
              url: `/advancements/v2/youth/ranks/${arg.rankId}/requirements`,
              method: 'POST',
              body: payload,
            });
          })
          .value();

        try {
          const results = await Promise.all(promises);
          const data = _.flatMap(
            results.map(res => {
              if ('error' in res) {
                throw res.error;
              } else {
                const result = res.data as PostBulkYouthRankRequirementsRes;
                return result;
              }
            }),
          );

          const errors = data.filter(item =>
            item.requirements.some(item => item.status === 'Fail'),
          );

          if (errors.length > 0) {
            throw { data: errors, status: 400 };
          }

          return {
            data,
          };
        } catch (err) {
          return { error: err };
        }
      },
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const orgGuids = _.uniq(arg.payload.map(item => item.organizationGuid));

        return orgGuids.flatMap(orgGuid => [
          {
            // invalidate the counts to approve tab
            type: tagTypes.OrganizationAdvDashboard,
            id: orgGuid,
          },
          {
            // refresh to approve list
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${orgGuid}`,
          },
        ]);
      },
    }),
    /**
     * @esbEndpoint POST /advancements/v2/youth/ranks/:rankId/requirements
     */
    postYouthRankRequirements: builder.mutation<
      PostBulkYouthRankRequirementsRes,
      { rankId: number; payload: PostBulkYouthRankRequirementsReq }
    >({
      query: ({ rankId, payload }) => ({
        url: `/advancements/v2/youth/ranks/${rankId}/requirements`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const orgGuids = _.uniq(arg.payload.map(item => item.organizationGuid));

        return orgGuids.flatMap(orgGuid => [
          {
            // invalidate the counts to approve tab
            type: tagTypes.OrganizationAdvDashboard,
            id: orgGuid,
          },
          {
            // refresh to approve list
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${orgGuid}`,
          },
        ]);
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/ranks/:rankId/requirements
     */
    getYouthRankRequirements: builder.query({
      query: ({ rankId, userId }) => ({
        url: `/advancements/v2/youth/${userId}/ranks/${rankId}/requirements`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { userId, rankId }) => [
        { type: tagTypes.YouthRankRequirements, id: `${userId}-${rankId}` },
      ],
    }),

    // PUBLIC ENDPOINTS (don't require authentication)
    /**
     * @esbEndpoint GET /advancements/v2/ranks
     */
    getRanks: builder.query<
      GetRanksRes,
      | {
          params: GetRanksReq;
        }
      | undefined
    >({
      query: () => ({
        url: `/advancements/v2/ranks`,
        method: 'GET',
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      providesTags: (result, _error, _arg) =>
        result
          ? [
              ...result.map(rank => ({
                type: tagTypes.Rank,
                id: rank.id,
              })),
              { type: tagTypes.Rank, id: 'Ranks-List' },
            ]
          : [],
    }),

    /**
     * @esbEndpoint POST /advancements/advancementHistory
     */
    getAdvancementHistory: builder.query<
      AdvancementHistoryItem[],
      AdvancementHistoryRequest
    >({
      query: arg => ({
        url: '/advancements/advancementHistory',
        method: 'POST',
        body: arg.body,
        params: arg.params,
      }),
      providesTags: (result, error, arg) => {
        if (error || !result) return [];

        return [
          {
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${arg.body.organizationGuid}`,
          },
          {
            type: tagTypes.AdvancementHistory,
            id: `Unit-${arg.body.organizationGuid}-AdvType-${arg.body.advancementType}`,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/adventures
     */
    getAdventures: builder.query<
      GetAdventuresRes,
      { params: GetAdventuresReq } | undefined
    >({
      query: () => ({
        url: `/advancements/v2/adventures`,
        method: 'GET',
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      providesTags: (result, _error, _arg) =>
        result?.adventures
          ? [
              ...result.adventures.map(adventure => ({
                type: tagTypes.Adventure,
                id: adventure.id,
              })),
              { type: tagTypes.Adventure, id: 'Adventures-List' },
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/ranks/:rankId/requirements?versionId=:versionId
     */
    getRankRequirements: builder.query<
      GetRankRequirementsRes,
      {
        rankId: number;
        params: GetRankRequirementsReq;
      }
    >({
      query: ({ rankId, params }) => ({
        url: `/advancements/v2/ranks/${rankId}/requirements`,
        params,
        method: 'GET',
        validateStatus: (
          response: { status: number },
          result: Record<string, unknown>,
        ) => response.status === 200 && !result.errorCode,
      }),
      providesTags: (result, error, arg) => {
        const versionId = arg.params?.versionId;
        const rankId = arg.rankId;

        return result && !error
          ? [{ type: tagTypes.RankRequirements, id: `${rankId}-${versionId}` }]
          : [];
      },
    }),

    /**
     * @esbEndpoint GET /persons/{personGuid}/renewalRelationships
     */
    getPersonRenewalRelationships: builder.query<
      GetRenewalRelationshipsResponse,
      {
        params?: GetRenewalRelationshipsRequest;
        personGuid: string;
      }
    >({
      query: ({ params, personGuid }) => ({
        url: `/persons/${personGuid}/renewalRelationships`,
        method: 'GET',
        params,
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.PersonRenewalRelationships,
                id: arg.personGuid,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint POST /persons/:personGuid/profilePicture
     */
    createProfilePicture: builder.mutation<
      void,
      {
        personGuid: string;
        body: PostProfilePictureRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/profilePicture`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              { type: tagTypes.CompletePersonProfile, id: arg.personGuid },
              {
                type: tagTypes.UnitRoster,
                id: `RosterAdult-${arg.personGuid}`,
              },
            ],
    }),

    /**
     * @esbEndpoint PUT /persons/:personGuid/profilePicture
     */
    updateProfilePicture: builder.mutation<
      void,
      {
        personGuid: string;
        body: PostProfilePictureRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/profilePicture`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              { type: tagTypes.CompletePersonProfile, id: arg.personGuid },
              {
                type: tagTypes.UnitRoster,
                id: `RosterAdult-${arg.personGuid}`,
              },
            ],
    }),

    /**
     * @esbEndpoint POST /advancements/:userId/pictureUpload
     */
    createPictureUpload: builder.mutation<
      PostPictureUploadResponse,
      {
        userId: number;
        body: PostPictureUploadRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/${arg.userId}/pictureUpload`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [{ type: tagTypes.PersonProfile, id: arg.userId }],
    }),

    /**
     * @esbEndpoint PUT /persons/v2/:personGuid/profile
     */
    updateAkelaProfile: builder.mutation<
      void,
      {
        personGuid: string;
        body: PutAkelaProfileRequest;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/profile`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              { type: tagTypes.CompletePersonProfile, id: arg.personGuid },
              {
                type: tagTypes.UnitRoster,
                id: `RosterAdult-${arg.personGuid}`,
              },
            ],
    }),

    /**
     * @esbEndpoint PUT /advancements/users/:userId
     */
    updateSBProfile: builder.mutation<
      void,
      {
        userId: number;
        body: PutSBProfileRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/users/${arg.userId}`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [{ type: tagTypes.PersonProfile, id: arg.userId }],
    }),

    /**
     * @esbEndpoint POST /persons/v2/:personGuid/deleteProfileInfo
     */
    deleteProfileInfo: builder.mutation<
      void,
      {
        personGuid: string;
        body: PostDeleteProfileInfoRequest;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/deleteProfileInfo`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              { type: tagTypes.CompletePersonProfile, id: arg.personGuid },
              {
                type: tagTypes.UnitRoster,
                id: `RosterAdult-${arg.personGuid}`,
              },
            ],
    }),

    /**
     * @esbEndpoint PUT /persons/:personGuid/subscriptions/:subscriptionId
     */
    updateSubscription: builder.mutation<
      void,
      {
        subscriptionId: number;
        personGuid: string;
        body: PUTSubscriptionRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/subscriptions/${arg.subscriptionId}`,
        method: 'PUT',
        body: arg.body,
      }),
    }),

    /**
     * @esbEndpoint POST /persons/:personGuid/subscriptions
     */
    createSubscription: builder.mutation<
      void,
      {
        personGuid: string;
        body: POSTSubscriptionRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/subscriptions`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [{ type: tagTypes.PersonSubscriptions, id: arg.personGuid }],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/youth/awards/{awardId}/requirements
     */
    updateYouthAwardRequirements: builder.mutation<
      PostAwardRequirementsResponse[],
      {
        awardId: number;
        body: PostAwardRequirementsRequest[];
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/awards/${arg.awardId}/requirements`,
        method: 'POST',
        body: arg.body,
      }),
    }),

    /**
     * @esbEndpoint PUT /advancements/youth/awards
     */
    createYouthAward: builder.mutation<
      PostAwardResponse,
      {
        body: PostAwardRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/youth/awards`,
        method: 'POST',
        body: arg.body,
      }),
    }),

    /**
     * @esbEndpoint PUT /advancements/youth/{userId}/awards/{awardId}/{userAwardId}
     */
    updateYouthAward: builder.mutation<
      void,
      {
        userId: number;
        awardId: number;
        userAwardId: number;
        body: PutUpdateAwardsRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/youth/${arg.userId}/awards/${arg.awardId}/${arg.userAwardId}`,
        method: 'PUT',
        body: arg.body,
      }),
    }),

    /**
     * @esbEndpoint PUT /advancements/unapprove
     */
    unapproveYouthAward: builder.mutation<
      void,
      {
        organizationGuid?: string;
        advancementType?: string;
        body: UnapproveAdvancementPayload[];
      }
    >({
      query: arg => ({
        url: `/advancements/unapprove`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        const statuses = _.chain([
          PendingItemStatus.LEADER_APPROVED,
          PendingItemStatus.AWARDED,
        ])
          .uniq()
          .sort()
          .join(',')
          .value();

        return error
          ? []
          : [
              {
                // invalidate the step #1 list
                type: tagTypes.AdvancementHistory,
                id: `Unit-List-${arg.organizationGuid}-${statuses}`,
              },
              {
                // invalidate the counts to approve tab
                type: tagTypes.OrganizationAdvDashboard,
                id: arg.organizationGuid,
              },
              arg.advancementType
                ? {
                    // refresh to approve list
                    type: tagTypes.AdvancementHistory,
                    id: `Unit-${arg.organizationGuid}-AdvType-${arg.advancementType}`,
                  }
                : {
                    // refresh to approve list
                    type: tagTypes.AdvancementHistory,
                    id: `Unit-List-${arg.organizationGuid}`,
                  },
            ];
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/activities
     */
    getActivities: builder.query<
      GetActivitiesResponse,
      {
        body: GetActivitesRequest;
        params: GetActivitesRequestParams;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/activities`,
        method: 'POST',
        params: arg.params,
        body: arg.body,
      }),
      providesTags: (result, error, arg) => {
        if (error) return [];

        const tags = [];
        if (arg.body.hostOrganizationGuid) {
          tags.push(
            tagFactory.Activities.orgActivities(arg.body.hostOrganizationGuid),
          );
        }

        if (arg.body.personGuid) {
          tags.push(tagFactory.Activities.youthActivities(arg.body.personGuid));
        }
        const tagPerActivity =
          result?.activities?.map(activity =>
            tagFactory.Activities.activityItem(activity.id),
          ) || [];
        tags.push(...tagPerActivity);

        return tags;
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/{userId}/userActivitySummary
     */
    getUserActivitySummary: builder.query<
      GetUserActivitySummaryResponse,
      {
        userId: number | string;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.userId}/userActivitySummary`,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];

        return [
          {
            id: `${arg.userId}`,
            type: tagTypes.UserActivitySummary,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint GET /persons/v2/{personGuid}/trainings/ypt
     */
    getPersonTrainingsYPT: builder.query<
      GetPersonTrainingYPTResponse,
      {
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/trainings/ypt`,
        method: 'GET',
      }),
      keepUnusedDataFor: 900, // 15 min
      providesTags: (_result, error, arg) => {
        if (error) return [];

        return [
          {
            id: arg.personGuid,
            type: tagTypes.PersonTrainingYPT,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/{organizationGuid}/denChiefs
     */
    getUnitDenChiefs: builder.query<
      GetUnitDenChiefsRes[],
      {
        orgGuids: string[];
      }
    >({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const requests = arg.orgGuids.map(orgGuid =>
          baseQuery({
            url: `/organizations/v2/${orgGuid}/denChief`,
            method: 'GET',
          }),
        );

        const responses = await Promise.all(requests);
        const datas = responses
          .filter(item => !!item.data)
          .map(item => item.data) as GetUnitDenChiefsRes[];
        return { data: datas };
      },
      providesTags: (result, error, arg) => {
        if (!result || error) return [];
        const orgs = _.chain(arg.orgGuids).uniq().sort().join(',').value();

        return [
          {
            type: tagTypes.UnitDenchiefs,
            id: `Unit-Denchiefs-${orgs}`,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/{organizationGuid}/subUnits
     */
    getMultipleSubUnits: builder.query<
      GetOrgSubUnitsResponse[],
      {
        orgGuids: string[];
      }
    >({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const requests = arg.orgGuids.map(orgGuid =>
          baseQuery({
            url: `/organizations/v2/units/${orgGuid}/subUnits`,
            method: 'GET',
          }),
        );

        const responses = await Promise.all(requests);
        const datas = responses
          .filter(item => !!item.data)
          .map(item => item.data) as GetOrgSubUnitsResponse[];
        return { data: datas };
      },
      providesTags: (result, error, arg) => {
        if (!result || error) return [];
        const orgs = _.chain(arg.orgGuids).uniq().sort().join(',').value();

        return [
          {
            type: tagTypes.SubUnits,
            id: `SubUnits-${orgs}`,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/{organizationGuid}/purchaseOrders
     */
    getUnitPurchaseOrders: builder.query<
      GetUnitPurchaseOrdersResponse,
      {
        organizationGuid: string;
        params: GetUnitPurchaseOrdersRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders`,
        method: 'GET',
        params: arg.params,
      }),
      transformResponse: (
        response: GetUnitPurchaseOrdersResponse,
      ): GetUnitPurchaseOrdersResponse =>
        response.map(po => ({
          ...po,
          status: po.status.toLowerCase(),
        })),
      providesTags: (result, error, arg) => {
        if (error || !result) return [];

        return [
          {
            type: tagTypes.PurchaseOrders,
            id: `Unit-PO-List-${arg.organizationGuid}`,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint GET /advancements/v2/{organizationGuid}/purchaseOrders/{purchaseOrderId}
     */
    getPurchaseOrderDetails: builder.query<
      GetPurchaseOrderDetailsResponse,
      {
        organizationGuid: string;
        purchaseOrderId: number;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders/${arg.purchaseOrderId}`,
        method: 'GET',
      }),
      transformResponse: (
        res: GetPurchaseOrderDetailsResponse,
      ): GetPurchaseOrderDetailsResponse => ({
        ...res,
        status: res.status.toLowerCase(),
      }),
      providesTags: (result, error, arg) => {
        if (error || !result) return [];

        return [
          {
            type: tagTypes.PurchaseOrders,
            id: `PO-${arg.purchaseOrderId}`,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint PUT /advancements/v2/{organizationGuid}/purchaseOrders/{id}
     */
    updatePurchaseOrder: builder.mutation<
      void,
      {
        organizationGuid: string;
        purchaseOrderId: number;
        body: UpdatePurchaseOrderDetailsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders/${arg.purchaseOrderId}`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.PurchaseOrders,
                id: `Unit-PO-List-${arg.organizationGuid}`,
              }, // for closed po list
              {
                type: tagTypes.PurchaseOrders,
                id: `PO-${arg.purchaseOrderId}`,
              }, // current po details
              // for the completed/approved advancement history.
            ],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/{organizationGuid}/purchaseOrders/{purchaseOrderId}/items
     */
    updateItemPurchaseOrder: builder.mutation<
      void,
      {
        organizationGuid: string;
        purchaseOrderId: number;
        body: AddItemPurchaseOrderReq[];
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders/${arg.purchaseOrderId}/items`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        const statuses = _.chain([
          PendingItemStatus.LEADER_APPROVED,
          PendingItemStatus.AWARDED,
        ])
          .uniq()
          .sort()
          .join(',')
          .value();

        return [
          {
            // invalidate the step #1 list
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${arg.organizationGuid}-${statuses}`,
          },
          {
            // invalidate the current po details
            type: tagTypes.PurchaseOrders,
            id: `PO-${arg.purchaseOrderId}`,
          },
          {
            // invalidate the po list for getting the current po
            type: tagTypes.PurchaseOrders,
            id: `Unit-PO-List-${arg.organizationGuid}`,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint POST /advancements/v2/:organizationGuid/purchaseOrders
     */
    createPurchaseOrder: builder.mutation<
      {
        message: string;
        id: number;
      },
      {
        organizationGuid: string;
        body: {
          userId: number;
        };
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.PurchaseOrders,
                id: `Unit-PO-List-${arg.organizationGuid}`,
              },
            ],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/${organizationGuid}/purchaseOrders/${poid}/items/delete
     */
    postDeleteItemPurchaseOrder: builder.mutation<
      {
        userId: number;
        advancementType: string;
        advancementId?: number;
        userAdvancementId?: number;
        status?: string;
        message?: string;
      },
      {
        organizationGuid: string;
        purchaseOrderId: number;
        body: AddItemPurchaseOrderReq[];
      }
    >({
      query: arg => ({
        url: `/advancements/v2/${arg.organizationGuid}/purchaseOrders/${arg.purchaseOrderId}/items/delete`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_results, error, arg) => {
        const statuses = _.chain([
          PendingItemStatus.LEADER_APPROVED,
          PendingItemStatus.AWARDED,
        ])
          .uniq()
          .sort()
          .join(',')
          .value();

        return error
          ? []
          : [
              {
                // refresh PO Details Item List
                type: tagTypes.PurchaseOrders,
                id: `PO-${arg.purchaseOrderId}`,
              },
              {
                // invalidate the step #1 list
                type: tagTypes.AdvancementHistory,
                id: `Unit-List-${arg.organizationGuid}-${statuses}`,
              },
            ];
      },
    }),

    /**
     * @esbEndpoint POST /organizations/:organizationGuid/advancementsReadyToBeAwarded
     */
    getAdvancementsReadyAward: builder.query<
      AdvancementReportData,
      {
        organizationGuid: string;
        body: GetOrgAdvancementsReadyToAwardRequest;
        params: GetOrgAdvancementsReadyToAwardParams;
      }
    >({
      query: arg => ({
        url: `/organizations/${arg.organizationGuid}/advancementsReadyToBeAwarded`,
        method: 'POST',
        body: arg.body,
        params: arg.params,
      }),
      providesTags: (_reslt, error, arg) => {
        if (error) return [];
        return [
          {
            type: tagTypes.OrgAdvancementsReadyToAward,
            id: arg.organizationGuid,
          },
        ];
      },
      transformResponse: (response: GetOrgAdvancementsReadyToAwardResponse) =>
        mapAdvancementReportDtoToModel(response || []),
    }),

    /*
     * @esbEndpoint GET /organizations/positions/:organizationGuid
     */
    getOrgPositions: builder.query<
      GetOrgPositionsResponse,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/positions/${arg.organizationGuid}`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.OrganizationPositions,
                id: arg.organizationGuid,
              },
            ],
    }),

    /**
     * @esbEndpoint POST /organizations/positions/:orgGuid/reassignPositions
     */
    postReassignPositions: builder.mutation<
      PostReassignPositionsResponse,
      {
        orgGuid: string;
        body: PostReassignPositionsRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/positions/${arg.orgGuid}/reassignPositions`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.OrganizationPositions,
                id: arg.orgGuid,
              },
            ],
    }),

    /**
     * @esbEndpoint POST /persons/positions/:personGuid/assignFunctional
     */
    postAssignFunctionalPositions: builder.mutation<
      PostAssignFunctionalResponse,
      {
        personGuid: string;
        body: PostAssignFunctionalRequest;
      }
    >({
      query: arg => ({
        url: `/persons/positions/${arg.personGuid}/assignFunctional`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.OrganizationPositions,
                id: arg.body.OrganizationGuid,
              },
            ],
    }),

    /**
     * @esbEndpoint POST /persons/positions/${personGuid}/expireFunctional
     */
    postExpireFunctionalPositions: builder.mutation<
      PostExpireFunctionalResponse,
      {
        personGuid: string;
        body: PostExpireFunctionalRequest;
      }
    >({
      query: arg => ({
        url: `/persons/positions/${arg.personGuid}/expireFunctional`,
        method: 'POST',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.OrganizationPositions,
                id: arg.body.OrganizationGuid,
              },
            ],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/events/:eventId/permissionSlipProfiles
     */
    postPermissionSlipProfiles: builder.mutation<
      PostPermissionSlipProfilesResponse,
      {
        eventId: number;
        body: PostPermissionSlipProfilesRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/events/${arg.eventId}/permissionSlipProfiles`,
        method: 'POST',
        body: arg.body,
        responseHandler: response => response.blob(),
      }),
    }),

    /**
     * @esbEndpoint GET /persons/:personGuid/membershipRegistrations
     */
    getMembershipRegistrations: builder.query<
      PostMembershipRegistrationsResponse,
      {
        personGuid: string;
        body: PostMembershipRegistrationsRequest;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/membershipRegistrations`,
        method: 'POST',
        body: arg.body,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];

        return [
          {
            id: arg.personGuid,
            type: tagTypes.MembershipRegistration,
          },
        ];
      },
    }),

    /**
     * @esbEndpoint PUT /persons/:personGuid/membershipRegistrations/:registrationId
     */
    updateMembershipRegistration: builder.mutation<
      UpdateMembershipRegistrationResponse,
      {
        personGuid: string;
        registrationId: number;
        body: UpdateMembershipRegistrationRequest;
      }
    >({
      query: arg => ({
        url: `/persons/${arg.personGuid}/membershipRegistrations/${arg.registrationId}`,
        method: 'PUT',
        body: arg.body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];

        return [
          { id: arg.personGuid, type: tagTypes.MembershipRegistration },
          {
            type: tagTypes.OrganizationRoster,
            id: `OrgYouths-Youth-${arg.personGuid}`,
          },
        ];
      },
    }),
  }),
});

export const endpoints = esbApi.endpoints;

export const {
  // Persons
  useGetPersonTrainingsYPTQuery,
  useLazyGetPersonTrainingsYPTQuery,
  useGetUserActivitySummaryQuery,
  useLazyGetUserActivitySummaryQuery,
  useGetMembershipRegistrationsQuery,
  useLazyGetMembershipRegistrationsQuery,
  useUpdateMembershipRegistrationMutation,
  // Org Dashboard
  useGetOrgAdvancementsDashboardQuery,
  useLazyGetOrgAdvancementsDashboardQuery,
  // Act Dashboard
  useGetOrgActivityDashboardQuery,
  useLazyGetOrgActivityDashboardQuery,
  // Advancements
  useGetRankRequirementsQuery,
  useGetRankDetailsQuery,
  useGetGeneralAdvancementDetailsQuery,
  useGetSSElectiveDetailsQuery,
  useGetYouthRankDetailsQuery,
  useGetYouthRankRequirementsQuery,
  useGetOrganizationProfileQuery,
  useGetBulkUserRankRequirementsQuery,
  // Merit Badges
  useGetMeritBadgeRequirementsQuery,
  useGetBulkUserMeritBadgeRequirementsQuery,
  useLazyGetBulkUserMeritBadgeRequirementsQuery,
  useGetYouthMeritBadgeDetailsQuery,
  // Collections
  useGetRanksQuery,
  useLazyGetRanksQuery,
  useGetAdventuresQuery,
  useLazyGetAdventuresQuery,
  // AdvancementHistory
  useGetAdvancementHistoryQuery,
  useLazyGetAdvancementHistoryQuery,
  // Adventures
  useGetAdventureRequirementsQuery,
  useLazyGetAdventureRequirementsQuery,
  useGetBulkUserAdventureRequirementsQuery,
  useLazyGetBulkUserAdventureRequirementsQuery,
  usePostYouthAdventuresRequirementsMutation,
  // Persons
  useGetPersonRenewalRelationshipsQuery,
  useLazyGetPersonRenewalRelationshipsQuery,
  useLazyGetOrganizationProfileQuery,
  useLazyGetPersonProfileQuery,
  useGetPersonProfileQuery,
  useGetCompletePersonProfileQuery,
  useGetUserSubscriptionsQuery,
  useGetUserMystProfileQuery,
  // Mutations
  usePostBulkYouthRankRequirementsMutation,
  usePostYouthMeritBadgesMutation,
  usePostBulkYouthMeritBadgeRequirementsMutation,
  usePostYouthRankRequirementsMutation,
  // Awards
  useCreateYouthAwardMutation,
  useUpdateYouthAwardRequirementsMutation,
  useUpdateYouthAwardMutation,
  useUnapproveYouthAwardMutation,
  // Activities
  useGetActivitiesQuery,
  useLazyGetActivitiesQuery,
  //Unit Den Chiefs
  useGetUnitDenChiefsQuery,
  //Sub units
  useGetMultipleSubUnitsQuery,
  // PurchaseOrders
  useGetUnitPurchaseOrdersQuery,
  useLazyGetUnitPurchaseOrdersQuery,
  useGetPurchaseOrderDetailsQuery,
  useLazyGetPurchaseOrderDetailsQuery,
  useUpdatePurchaseOrderMutation,
  useUpdateItemPurchaseOrderMutation,
  useCreatePurchaseOrderMutation,
  usePostDeleteItemPurchaseOrderMutation,
  // Ready to award
  useGetAdvancementsReadyAwardQuery,
  useLazyGetAdvancementsReadyAwardQuery,
  //  OrgPositions
  useGetOrgPositionsQuery,
  useLazyGetOrgPositionsQuery,
  usePostReassignPositionsMutation,
  usePostAssignFunctionalPositionsMutation,
  usePostExpireFunctionalPositionsMutation,
  // Permission Slip
  usePostPermissionSlipProfilesMutation,
  // Profile
  useCreateProfilePictureMutation,
  useUpdateProfilePictureMutation,
  useCreatePictureUploadMutation,
  useUpdateAkelaProfileMutation,
  useUpdateSBProfileMutation,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useDeleteProfileInfoMutation,
} = esbApi;
